<template>
    <div class="ls-flow-header--container">
        <div class="ls-flow-header--title">
            <h2>{{ title }}</h2>
            <a :href="url" target="_blank" test-id="url-link">
                <img :src="require('./assets/new-tab.svg')" />
            </a>
        </div>
        <div
            class="ls-flow-header--customer"
            test-id="customer"
            @mouseenter="setIsHovered"
            @mouseleave="setIsHovered"
            @click="onClick"
        >
            <LsIcon
                type="chevron"
                :color="isHovered ? 'secondary' : 'gray-primary'"
                class="ls-flow-header--chevron"
            />
            <span>{{ customer.user | fullName }}</span>
            <span v-if="property" test-id="address">{{ addressString }}</span>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types';
import _ from 'lodash';
import { LsIcon } from '@/components/common';
import { lsHelpers } from '@/helpers';

export default {
    name: 'LsFlowHeader',
    components: { LsIcon },
    props: {
        customer: VueTypes.shape({
            id: VueTypes.string.isRequired,
            properties: VueTypes.array.isRequired,
            user: VueTypes.shape({
                first_name: VueTypes.string.isRequired,
                last_name: VueTypes.string.isRequired,
            }).isRequired.loose,
        }).isRequired.loose,
        property: VueTypes.shape({
            street: VueTypes.string.isRequired,
            city: VueTypes.string.isRequired,
            state: VueTypes.string.isRequired,
            zip: VueTypes.string.isRequired,
        }).loose,
        from: VueTypes.object.def({}),
    },
    data() {
        return {
            isHovered: false,
        };
    },
    computed: {
        addressString() {
            const { street, city, state, zip } = this.property;

            return `(${street}, ${city}, ${state} ${zip})`;
        },
        firstPropertyId() {
            const { properties } = this.customer;

            if (!_.size(properties)) {
                return;
            }
            return _.head(properties).id;
        },
        url() {
            const urls = {
                addProperty:
                    'https://support.lawnstarter.com/hc/en-us/articles/360001825699-Adding-a-Property-to-a-Current-Customer',
                cancel:
                    'https://support.lawnstarter.com/hc/en-us/articles/360001792300-How-to-Cancel-or-Pause-an-Account',
                changeFrequency: 'https://support.lawnstarter.com/hc/en-us/articles/115000396824',
                changeProvider:
                    'https://support.lawnstarter.com/hc/en-us/articles/360001796980-Bulk-Priority-Redistribution-for-Lawn-Mowing',
                changeScope:
                    'https://support.lawnstarter.com/hc/en-us/articles/360001796660-Scope-Change-Request-',
                dispute: 'https://support.lawnstarter.com/hc/en-us/articles/360012220360',
                pastDisputes: 'https://support.lawnstarter.com/hc/en-us/articles/360012220360',
                changeServiceWindow:
                    'https://support.lawnstarter.com/hc/en-us/articles/360012130519-Change-Service-Window',
                jobNotes:
                    'https://support.lawnstarter.com/hc/en-us/articles/360001661260-Admin-Notes',
                pause:
                    'https://support.lawnstarter.com/hc/en-us/articles/360002592920-Web-Team-Cancellation-Pause-Request-UPDATED-',
                reactivate:
                    'https://support.lawnstarter.com/hc/en-us/articles/360001792240-Reactivation',
                serviceNotes:
                    'https://support.lawnstarter.com/hc/en-us/articles/360001661260-Admin-Notes',
                skip:
                    'https://support.lawnstarter.com/hc/en-us/articles/360001743360-Web-Team-Skip-Service-Request-Updated-',
                unpause:
                    'https://support.lawnstarter.com/hc/en-us/articles/360001792240-Reactivation-or-Unpause',
                conversation:
                    'https://support.lawnstarter.com/hc/en-us/articles/360012449900-Message-My-Crew-Convo-Feature',
            };

            return urls[_.get(this, '$route.name')] || '';
        },
        title() {
            return _.startCase(_.get(this, '$route.name'));
        },
    },
    methods: {
        setIsHovered() {
            this.isHovered = !this.isHovered;
        },
        onClick() {
            const hasLastRoute = _.get(this.from, 'name');
            // if no last route and on AddPropertyView
            if (!hasLastRoute && !this.property) {
                // and they have at least one property
                if (this.firstPropertyId) {
                    return this.$router.replace({
                        name: 'property',
                        params: {
                            customer_id: this.customer.id,
                            property_id: this.firstPropertyId,
                        },
                    });
                    // otherwise, go directly to search
                } else {
                    return this.$router.replace({
                        name: 'customerSearch',
                    });
                }
            }
            // if there is last route and on AddPropertyView, go back to previous route
            if (!this.property) {
                return this.$router.replace(this.from);
            }
            // otherwise go back to parent view (normal flow views)
            lsHelpers.goToParentView();
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-flow-header--container {
    > h2 {
        font-weight: $semi-bold;
    }
}

.ls-flow-header--chevron {
    transform: rotate(180deg);
    margin: 1px 5px 0 0;
}

.ls-flow-header--title {
    display: flex;

    > a {
        padding: 3px 0 6px 3px;

        > img {
            &:hover {
                opacity: $hover-opacity;
            }
        }
    }
}

.ls-flow-header--customer {
    margin-left: -4px;
    display: flex;
    cursor: pointer;

    > span:last-child {
        padding-left: 4px;
    }

    &:hover {
        color: $secondary;
    }
}
</style>
